/**
 * Quiet hours and the system alert address list.
 *
 * Quiet hours hold non-critical email back inside a nightly window; the times
 * need both ends before the API will accept them, so this block has its own
 * small Apply rather than firing on every keystroke.
 *
 * System Alert Emails is the one field of the old Notification Defaults tab
 * that anything actually reads, so it moves here rather than dying with it
 * (scope doc §D.1). It lives on PlatformSettings, so it saves through the tab's
 * existing platform-settings props.
 */
import { useEffect, useState } from 'react';
import type { NotificationSettingsUpdate } from '../../../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { Textarea } from '../../../ui/textarea';
import { Checkbox } from '../../../ui/checkbox';
import { Clock, Loader2 } from 'lucide-react';
import type { MasterSwitches } from './types';

interface QuietHoursDraft {
  enabled: boolean;
  start: string;
  end: string;
  applyToCritical: boolean;
}

const toDraft = (master: MasterSwitches): QuietHoursDraft => ({
  enabled: master.quietHoursEnabled,
  start: master.quietHoursStart,
  end: master.quietHoursEnd,
  applyToCritical: master.quietHoursApplyToCritical,
});

interface NotificationDeliveryCardProps {
  master: MasterSwitches;
  canConfigure: boolean;
  saving: boolean;
  onChange: (patch: NotificationSettingsUpdate, message: string) => void;
  systemAlertEmails: string;
  onSystemAlertEmailsChange: (value: string) => void;
  onSaveSystemAlertEmails: () => void;
  savingPlatform: boolean;
}

export function NotificationDeliveryCard({
  master,
  canConfigure,
  saving,
  onChange,
  systemAlertEmails,
  onSystemAlertEmailsChange,
  onSaveSystemAlertEmails,
  savingPlatform,
}: NotificationDeliveryCardProps) {
  const [draft, setDraft] = useState<QuietHoursDraft>(() => toDraft(master));

  // Re-sync whenever the server row changes underneath us (initial load, or a
  // rejected write snapping back).
  useEffect(() => {
    setDraft(toDraft(master));
  }, [master]);

  const changed =
    draft.enabled !== master.quietHoursEnabled
    || draft.start !== master.quietHoursStart
    || draft.end !== master.quietHoursEnd
    || draft.applyToCritical !== master.quietHoursApplyToCritical;

  const incomplete = draft.enabled && (!draft.start || !draft.end);
  const disabled = !canConfigure || saving;

  const apply = () => {
    onChange(
      {
        quiet_hours_enabled: draft.enabled,
        quiet_hours_start: draft.start || null,
        quiet_hours_end: draft.end || null,
        quiet_hours_apply_to_critical: draft.applyToCritical,
      },
      draft.enabled ? 'Quiet hours updated' : 'Quiet hours switched off',
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Quiet hours &amp; alert addresses
        </CardTitle>
        <CardDescription>
          Hold notification email back overnight, and choose who receives system-wide alerts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="quiet-hours-enabled" className="font-medium">Quiet hours</Label>
              <p className="text-sm text-muted-foreground">
                Email raised inside the window is held until it closes.
              </p>
            </div>
            <Switch
              id="quiet-hours-enabled"
              checked={draft.enabled}
              disabled={disabled}
              onCheckedChange={(checked) => setDraft({ ...draft, enabled: checked })}
            />
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="quiet-hours-start" className="text-sm">From</Label>
              <Input
                id="quiet-hours-start"
                type="time"
                className="w-36"
                value={draft.start}
                disabled={disabled || !draft.enabled}
                onChange={(e) => setDraft({ ...draft, start: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="quiet-hours-end" className="text-sm">To</Label>
              <Input
                id="quiet-hours-end"
                type="time"
                className="w-36"
                value={draft.end}
                disabled={disabled || !draft.enabled}
                onChange={(e) => setDraft({ ...draft, end: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Checkbox
                id="quiet-hours-critical"
                checked={draft.applyToCritical}
                disabled={disabled || !draft.enabled}
                onCheckedChange={(checked) =>
                  setDraft({ ...draft, applyToCritical: checked === true })
                }
              />
              <Label htmlFor="quiet-hours-critical" className="text-sm font-normal">
                Hold critical notifications too
              </Label>
            </div>
            <Button
              variant="outline"
              disabled={disabled || !changed || incomplete}
              onClick={apply}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply quiet hours
            </Button>
          </div>
          {incomplete && (
            <p className="text-sm text-destructive">
              Set both a start and an end time before enabling quiet hours.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="systemAlertEmails">System alert emails</Label>
          <Textarea
            id="systemAlertEmails"
            value={systemAlertEmails}
            onChange={(e) => onSystemAlertEmailsChange(e.target.value)}
            placeholder="admin@company.com, alerts@company.com"
            rows={2}
            disabled={!canConfigure}
          />
          <p className="text-sm text-muted-foreground">
            Comma-separated addresses that receive system-wide alerts.
          </p>
          <Button
            variant="outline"
            disabled={!canConfigure || savingPlatform}
            onClick={onSaveSystemAlertEmails}
          >
            {savingPlatform && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save alert addresses
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
